import React from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ascend, sortWith } from 'ramda';
import { i18n } from 'i18n';
import PCPipe from '../MineModel/Pipes/PCPipe';
import { PipeUsage } from './types';

interface PipeUsageTableProps {
  pipes: PCPipe[];
  usage: Record<string, PipeUsage>;
  onChange: (pipeId: string, usage: PipeUsage) => void;
  formatNumber: (value: number, decimalPlaces: number) => string;
  disabled?: boolean;
}

const byRating = sortWith<PCPipe>([ascend((pipe) => pipe.pressureRating), ascend((pipe) => pipe.name)]);

const usageLabels = (): Record<PipeUsage, string> => ({
  none: i18n._('Not used'),
  level: i18n._('Level'),
  borehole: i18n._('Borehole'),
});

const PipeUsageTable = ({
  pipes,
  usage,
  onChange,
  formatNumber,
  disabled,
}: PipeUsageTableProps) => {
  const labels = usageLabels();
  return (
    <TableContainer sx={{ maxHeight: 280, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>{i18n._('Pipe')}</TableCell>
            <TableCell align="right">{i18n._('Inside diameter (mm)')}</TableCell>
            <TableCell align="right">{`${i18n._('Pressure Rating')} (kPa)`}</TableCell>
            <TableCell>{i18n._('Use as')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {byRating(pipes).map((pipe) => (
            <TableRow key={pipe.pipeId}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 12, height: 12, borderRadius: '2px', bgcolor: pipe.colour, flexShrink: 0,
                  }}
                  />
                  {pipe.name}
                </Box>
              </TableCell>
              <TableCell align="right">{formatNumber(pipe.insideDiameter * 1000, 1)}</TableCell>
              <TableCell align="right">{formatNumber(pipe.pressureRating, 0)}</TableCell>
              <TableCell sx={{ py: 0.25 }}>
                <Select
                  size="small"
                  variant="standard"
                  color="secondary"
                  value={usage[pipe.pipeId] ?? 'none'}
                  disabled={disabled}
                  onChange={(event) => onChange(pipe.pipeId, event.target.value as PipeUsage)}
                  inputProps={{ 'aria-label': i18n._('Use {pipe} as', { pipe: pipe.name }) }}
                  sx={{ minWidth: 110, fontSize: 13 }}
                >
                  {(Object.keys(labels) as PipeUsage[]).map((key) => <MenuItem key={key} value={key}>{labels[key]}</MenuItem>)}
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PipeUsageTable;
